import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Plus, Pencil, Trash2, FileText, Stethoscope, AlertTriangle, Activity, FlaskConical, Image as ImageIcon, Calendar, Phone, User, FilePlus2, ShieldAlert, NotebookPen, FileBadge2, Save, Printer, Eye, Syringe, Send, ChevronRight, ChevronDown, ChevronUp, Receipt, Camera } from "lucide-react";
import { toast } from "sonner";
import { ReferralDialog } from "@/pages/Referrals";
import { useAuth } from "@/context/AuthContext";
import { vitalsSummary } from "@/components/VitalsForm";
import ClinicalNotesForm, { EMPTY_NOTES } from "@/components/ClinicalNotesForm";
import LabAttachmentsPanel from "@/components/LabAttachmentsPanel";
import MedicalCertificatePreview, { DEFAULT_TEMPLATE, VACCINE_TEMPLATE } from "@/components/MedicalCertificatePreview";
import ScheduleVisitDialog from "@/components/ScheduleVisitDialog";

const RECORD_TYPES = [
  { value: "note", label: "Clinical Note", icon: FileText, tone: "bg-slate-100 text-slate-700" },
  { value: "diagnosis", label: "Diagnosis", icon: Stethoscope, tone: "bg-blue-50 text-blue-700" },
  { value: "allergy", label: "Allergy", icon: ShieldAlert, tone: "bg-red-50 text-red-700" },
  { value: "vitals", label: "Vitals", icon: Activity, tone: "bg-emerald-50 text-emerald-700" },
  { value: "lab", label: "Lab Result", icon: FlaskConical, tone: "bg-amber-50 text-amber-700" },
  { value: "imaging", label: "Imaging", icon: ImageIcon, tone: "bg-violet-50 text-violet-700" },
  { value: "history", label: "History", icon: AlertTriangle, tone: "bg-orange-50 text-orange-700" },
  { value: "vaccination", label: "Vaccination", icon: Syringe, tone: "bg-teal-50 text-teal-700" },
];

function typeMeta(v) { return RECORD_TYPES.find((t) => t.value === v) || RECORD_TYPES[0]; }

export default function PatientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const role = user?.role || "";
  const canViewClinicalNotes = role === "doctor";
  // Withdrawing an issued certificate is limited to the issuing side, not the
  // front desk — reception can create and print them but not retract them.
  const _roles = Array.isArray(user?.roles) && user.roles.length ? user.roles : [role];
  const canDeleteCert = _roles.some((r) => ["doctor", "admin", "hospital_admin"].includes(r));
  const otherHospitals = (user?.memberships || []).filter((m) => m.hospital_id !== user?.active_hospital_id);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendTarget, setSendTarget] = useState("");
  const [sendPreview, setSendPreview] = useState(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [visitDate, setVisitDate] = useState("");
  const [visitTime, setVisitTime] = useState("");
  const [savingVisit, setSavingVisit] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [sendMode, setSendMode] = useState("new");
  const [sendBusy, setSendBusy] = useState(false);
  const [tab, setTab] = useState("records");
  const [drilled, setDrilled] = useState(false);
  const [patient, setPatient] = useState(null);
  const [records, setRecords] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [vitalsHistory, setVitalsHistory] = useState([]);
  const [notesHistory, setNotesHistory] = useState([]);
  const [expandedNotes, setExpandedNotes] = useState(() => new Set());
  const toggleNote = (nid) => setExpandedNotes((prev) => {
    const s = new Set(prev);
    if (s.has(nid)) s.delete(nid); else s.add(nid);
    return s;
  });
  const [labResults, setLabResults] = useState([]);
  const [posTransactions, setPosTransactions] = useState([]);
  const [recOpen, setRecOpen] = useState(false);
  const [recForm, setRecForm] = useState({ type: "diagnosis", title: "", content: "", date: new Date().toISOString().slice(0, 10) });
  const [recEditingId, setRecEditingId] = useState(null);
  const [refOpen, setRefOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteEditingId, setNoteEditingId] = useState(null);
  const [noteForm, setNoteForm] = useState({ ...EMPTY_NOTES });
  const [noteSaving, setNoteSaving] = useState(false);
  const [medCerts, setMedCerts] = useState([]);
  const [mcOpen, setMcOpen] = useState(false);
  const [mcPreviewOpen, setMcPreviewOpen] = useState(false);
  const [activeCert, setActiveCert] = useState(null);
  const [printSettings, setPrintSettings] = useState({});
  const [doctorRxProfile, setDoctorRxProfile] = useState({});
  const [mcSaving, setMcSaving] = useState(false);
  const [mcType, setMcType] = useState("medical");
  const [mcEditId, setMcEditId] = useState(null); // null = creating new; id = editing an issued cert
  const [mcForm, setMcForm] = useState({
    issue_date: new Date().toISOString().slice(0, 10),
    diagnosis: "",
    recommendations: "",
    remarks: "",
    purpose: "",
    rest_days: 0,
    body_template: DEFAULT_TEMPLATE,
  });

  const loadAll = async () => {
    try {
      const requests = [
        api.get(`/patients/${id}`),
        api.get(`/patient-records/patient/${id}`),
        // Server-side filter: this used to pull the WHOLE hospital's history
        // (~17 MB at the largest clinic) and filter in the browser.
        api.get(`/prescriptions?patient_id=${id}`),
        api.get(`/referrals?patient_id=${id}`),
        api.get(`/patients/${id}/vitals`).catch(() => ({ data: [] })),
        api.get(`/patients/${id}/lab-results`).catch(() => ({ data: [] })),
        api.get(`/patients/${id}/medical-certificates`).catch(() => ({ data: [] })),
        api.get(`/print-settings`).catch(() => ({ data: {} })),
        api.get(`/doctor-rx-profile/me`).catch(() => ({ data: {} })),
      ];
      if (canViewClinicalNotes) {
        requests.push(api.get(`/patients/${id}/clinical-notes`).catch(() => ({ data: [] })));
      }
      requests.push(api.get(`/pos/transactions`, { params: { scope: "all" } }).catch(() => ({ data: [] })));
      const results = await Promise.all(requests);
      const [p, r, rx, rf, vh, lr, mc, ps, drx] = results;
      // Base requests occupy indices 0-8; clinical-notes (if any) then pos-transactions
      // are pushed after, so resolve them by cursor rather than hardcoded indices.
      let cursor = 9;
      let nhData = { data: [] };
      if (canViewClinicalNotes) { nhData = results[cursor++]; }
      const txData = results[cursor]?.data || [];
      setPatient(p.data);
      setRecords(Array.isArray(r.data) ? r.data : []);
      const allRx = Array.isArray(rx.data) ? rx.data : [];
      setPrescriptions(
        allRx.filter(
          (x) =>
            x.patient_id === id &&
            x.status !== "deleted"
        )
      );
      const allRef = Array.isArray(rf.data) ? rf.data : [];
      setReferrals(allRef.filter((x) => x.patient_id === id));
      setVitalsHistory(Array.isArray(vh?.data) ? vh.data : []);
      setLabResults(Array.isArray(lr?.data) ? lr.data : []);
      setMedCerts(Array.isArray(mc?.data) ? mc.data : []);
      setPrintSettings(ps?.data || {});
      setDoctorRxProfile(drx?.data || {});
      setNotesHistory(canViewClinicalNotes && Array.isArray(nhData?.data) ? nhData.data : []);
      setPosTransactions(Array.isArray(txData) ? txData.filter((t) => t.patient_id === id) : []);
    } catch {
      toast.error("Could not load patient");
    }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [id]);

  const openNewRec = () => {
    setRecForm({ type: "diagnosis", title: "", content: "", date: new Date().toISOString().slice(0, 10) });
    setRecEditingId(null);
    setRecOpen(true);
  };
  const openEditRec = (r) => {
    setRecForm({ type: r.type, title: r.title, content: r.content || "", date: (r.date || "").slice(0, 10) });
    setRecEditingId(r.id);
    setRecOpen(true);
  };
  const saveRec = async () => {
    try {
      const payload = { ...recForm, patient_id: id, date: recForm.date ? new Date(recForm.date).toISOString() : undefined };
      if (recEditingId) {
        await api.put(`/patient-records/${recEditingId}`, payload);
      } else {
        await api.post("/patient-records", payload);
      }
      toast.success("Record saved");
      setRecOpen(false);
      loadAll();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    }
  };
  const delRec = async (rid) => {
    if (!window.confirm("Delete this record?")) return;
    await api.delete(`/patient-records/${rid}`);
    toast.success("Deleted");
    loadAll();
  };

  const latestVitalsObjectiveText = () => {
    const latest = Array.isArray(vitalsHistory) && vitalsHistory.length ? vitalsHistory[0] : null;
    const v = latest?.vitals || {};

    const lines = ["Vitals:"];
    if (v.blood_pressure) lines.push(`BP: ${v.blood_pressure}`);
    if (v.heart_rate) lines.push(`HR: ${v.heart_rate}`);
    if (v.respiratory_rate) lines.push(`RR: ${v.respiratory_rate}`);
    if (v.temperature) lines.push(`Temp: ${v.temperature}`);
    if (v.oxygen_saturation) lines.push(`SpO2: ${v.oxygen_saturation}`);
    if (v.weight) lines.push(`Weight: ${v.weight}`);
    if (v.height) lines.push(`Height: ${v.height}`);
    if (v.bmi) lines.push(`BMI: ${v.bmi}`);

    if (lines.length === 1) {
      lines.push("No vitals recorded.");
    }

    lines.push("");
    lines.push("Physical Exam / Objective Findings:");

    return lines.join("\n");
  };

  const openNewClinicalNote = () => {
    setNoteEditingId(null);
    setNoteForm({ ...EMPTY_NOTES, objective: latestVitalsObjectiveText() });
    setNoteOpen(true);
  };

  const openEditClinicalNote = (record) => {
    setNoteEditingId(record.id);
    const cn = record.clinical_notes || {};
    const hasSoap = cn.subjective || cn.objective || cn.assessment || cn.plan || cn.notes;
    if (hasSoap) {
      setNoteForm({ ...EMPTY_NOTES, ...cn });
    } else {
      // Legacy "note" record — seed the SOAP form from its title/content so the doctor can add detail.
      setNoteForm({ ...EMPTY_NOTES, assessment: record.title || "", notes: record.content || "" });
    }
    setNoteOpen(true);
  };

  const openNewMedCert = () => {
    const latestDiagnosis =
      (records || []).find((r) => r.type === "diagnosis")?.content ||
      (notesHistory || []).find((r) => r.clinical_notes?.assessment)?.clinical_notes?.assessment ||
      "";

    setMcType("medical");
    setMcForm({
      issue_date: new Date().toISOString().slice(0, 10),
      date_text: "",
      diagnosis: latestDiagnosis || "",
      recommendations: "",
      remarks: "",
      purpose: "",
      rest_days: 0,
      body_template: DEFAULT_TEMPLATE,
    });
    setMcEditId(null);
    setActiveCert(null);
    setMcOpen(true);
  };

  const openNewVaccineCert = () => {
    setMcType("vaccine");
    setMcForm({
      issue_date: new Date().toISOString().slice(0, 10),
      date_text: "",
      diagnosis: "",
      recommendations: "",
      remarks: "",
      purpose: "",
      rest_days: 0,
      body_template: VACCINE_TEMPLATE,
    });
    setMcEditId(null);
    setActiveCert(null);
    setMcOpen(true);
  };

  const deleteMedCert = async (cert) => {
    const label = cert.issue_date || (cert.created_at || "").slice(0, 10);
    if (!window.confirm(
      `Delete the certificate dated ${label}?\n\n` +
      "It will be removed from this patient's records. A record that it was " +
      "issued and withdrawn is kept for audit."
    )) return;
    const reason = window.prompt("Reason (optional) — e.g. issued in error, superseded") || "";
    try {
      await api.delete(
        `/patients/${id}/medical-certificates/${cert.id}?reason=${encodeURIComponent(reason)}`
      );
      toast.success("Certificate deleted");
      const list = await api.get(`/patients/${id}/medical-certificates`);
      setMedCerts(list.data || []);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Could not delete certificate");
    }
  };

  // Re-open an already-issued certificate in the editor so the doctor can amend
  // and reprint it.
  const openEditMedCert = (cert) => {
    const type = cert.cert_type === "vaccine" ? "vaccine" : "medical";
    setMcType(type);
    setMcForm({
      issue_date: (cert.issue_date || new Date().toISOString().slice(0, 10)).slice(0, 10),
      date_text: cert.date_text || "",
      diagnosis: cert.diagnosis || "",
      recommendations: cert.recommendations || "",
      remarks: cert.remarks || "",
      purpose: cert.purpose || "",
      rest_days: cert.rest_days || 0,
      body_template: cert.body_template || (type === "vaccine" ? VACCINE_TEMPLATE : DEFAULT_TEMPLATE),
    });
    setMcEditId(cert.id);
    setActiveCert(cert);
    setMcOpen(true);
  };

  const saveMedCert = async (previewAfter = true) => {
    setMcSaving(true);
    try {
      const payload = { ...mcForm, cert_type: mcType };
      const { data } = mcEditId
        ? await api.put(`/patients/${id}/medical-certificates/${mcEditId}`, payload)
        : await api.post(`/patients/${id}/medical-certificates`, payload);
      toast.success(mcEditId ? "Certificate updated" : (mcType === "vaccine" ? "Vaccine certificate saved" : "Medical certificate saved"));
      setActiveCert(data);
      const list = await api.get(`/patients/${id}/medical-certificates`);
      setMedCerts(Array.isArray(list.data) ? list.data : []);
      setMcOpen(false);
      setMcEditId(null);
      if (previewAfter) setMcPreviewOpen(true);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally {
      setMcSaving(false);
    }
  };

  const openMedCert = (cert) => {
    setActiveCert(cert);
    setMcPreviewOpen(true);
  };

  const openSend = () => {
    setSendTarget("");
    setSendPreview(null);
    setSendMode("new");
    setSendOpen(true);
  };

  const openSchedule = () => {
    setVisitDate((patient?.next_visit_date || "").slice(0, 10));
    setScheduleOpen(true);
  };
  const uploadPhoto = async (fileObj) => {
    if (!fileObj) return;
    if (!fileObj.type?.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setPhotoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", fileObj);
      const { data } = await api.post(`/patients/${id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPatient((p) => ({ ...p, photo_url: data.photo_url }));
      toast.success("Patient photo saved");
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally {
      setPhotoBusy(false);
    }
  };
  const saveVisit = async () => {
    setSavingVisit(true);
    try {
      await api.put(`/patients/${id}/next-visit`, { next_visit_date: visitDate, next_visit_time: visitTime });
      toast.success(visitDate ? "Next visit scheduled" : "Next visit cleared");
      setScheduleOpen(false);
      await loadAll();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally {
      setSavingVisit(false);
    }
  };
  const runSendPreview = async (hid) => {
    setSendTarget(hid);
    setSendPreview(null);
    setSendMode("new");
    if (!hid) return;
    setSendBusy(true);
    try {
      const { data } = await api.post(`/patients/${id}/send-to-hospital/preview`, { target_hospital_id: hid });
      setSendPreview(data);
      setSendMode(data?.match ? "" : "new");
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally {
      setSendBusy(false);
    }
  };
  const confirmSend = async () => {
    if (!sendTarget) { toast.error("Select a destination hospital"); return; }
    if (sendPreview?.match && !sendMode) { toast.error("Choose merge or create new"); return; }
    setSendBusy(true);
    try {
      const { data } = await api.post(`/patients/${id}/send-to-hospital`, {
        target_hospital_id: sendTarget,
        mode: sendMode || "new",
        target_patient_id: sendMode === "merge" ? (sendPreview?.match?.id || "") : "",
      });
      const c = data?.copied || {};
      const total = Object.values(c).reduce((a, b) => a + (b || 0), 0);
      toast.success(`Sent to ${data?.target_hospital_name || "hospital"} — ${total} record(s) copied`);
      setSendOpen(false);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally {
      setSendBusy(false);
    }
  };

  const saveClinicalNote = async () => {
    setNoteSaving(true);
    try {
      if (noteEditingId) {
        await api.put(`/clinical-notes/${noteEditingId}`, noteForm);
        toast.success("Clinical note updated");
      } else {
        await api.post(`/patients/${id}/clinical-notes`, noteForm);
        toast.success("Clinical note added");
      }
      setNoteOpen(false);
      setNoteEditingId(null);
      setNoteForm({ ...EMPTY_NOTES });
      loadAll();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally {
      setNoteSaving(false);
    }
  };

  const deleteClinicalNote = async (recordId) => {
    if (!window.confirm("Delete this clinical note?\n\nIt will be removed from the record but kept in the audit log.")) return;
    try {
      await api.delete(`/clinical-notes/${recordId}`);
      toast.success("Clinical note deleted");
      loadAll();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    }
  };

  const MOBILE_SECTIONS = [
    { key: "records", label: "Medical Records", count: records.length, icon: FileText },
    { key: "vitals", label: "Vitals History", count: vitalsHistory.length, icon: Activity },
    ...(canViewClinicalNotes ? [{ key: "notes", label: "Clinical Notes", count: notesHistory.length, icon: NotebookPen }] : []),
    { key: "labs", label: "Lab Results", count: labResults.length, icon: FlaskConical },
    { key: "rx", label: "Prescriptions", count: prescriptions.length, icon: FilePlus2 },
    { key: "referrals", label: "Referrals", count: referrals.length, icon: Send },
    { key: "medcerts", label: "Certificates", count: medCerts.length, icon: FileBadge2 },
    { key: "billing", label: "Billing / POS", count: posTransactions.length, icon: Receipt },
  ];

  if (!patient) return <div className="p-8 text-slate-500">Loading...</div>;

  return (
    <div className="p-6 md:p-8 max-w-6xl" data-testid="patient-detail-page">
      <Button asChild variant="ghost" className="mb-3">
        <Link to="/patients"><ArrowLeft size={14} className="mr-2" /> Back to Patients</Link>
      </Button>

      <Card className="p-5 border-slate-200 mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <label className="relative w-14 h-14 rounded-full overflow-hidden bg-blue-50 text-blue-700 flex items-center justify-center cursor-pointer group shrink-0" title="Take or upload patient photo" data-testid="patient-photo">
                {patient.photo_url ? (
                  <img src={patient.photo_url} alt={patient.full_name || "patient"} className="w-full h-full object-cover" />
                ) : (
                  <User size={22} />
                )}
                <span className="absolute inset-0 bg-black/45 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                  <Camera size={16} className="text-white" />
                </span>
                <input type="file" accept="image/*" className="hidden" disabled={photoBusy} onChange={(e) => { uploadPhoto(e.target.files?.[0]); e.target.value = ""; }} data-testid="patient-photo-input" />
              </label>
              <div>
                <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: "Work Sans" }}>{patient.full_name}</h1>
                <div className="text-xs text-slate-500">{patient.age || "—"} yrs · {patient.gender || "—"}{patient.birthday ? ` · DOB ${patient.birthday}` : ""}</div>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
              <div className="flex items-center gap-2 text-slate-700"><Phone size={12} className="text-slate-400" /><span className="mono text-xs">{patient.contact_number || "—"}</span></div>
              <div className="flex items-center gap-2 text-slate-700"><Calendar size={12} className="text-slate-400" />Next visit: <span className="font-medium">{patient.next_visit_date || "—"}</span></div>
              <div className="col-span-2 text-slate-700">{patient.address || ""}</div>
              {patient.notes ? <div className="col-span-2 text-xs text-slate-500 italic">"{patient.notes}"</div> : null}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <Button onClick={() => nav(`/prescriptions/new?patient=${id}&returnTo=${encodeURIComponent(`/patients/${id}`)}`)} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-new-rx-for-patient">
              <FilePlus2 size={14} className="mr-2" /> New Prescription
            </Button>
            <Button variant="outline" onClick={openNewMedCert} data-testid="btn-new-medcert-for-patient">
              <FileBadge2 size={14} className="mr-2" /> Create Medical Certificate
            </Button>
            <Button variant="outline" onClick={openNewVaccineCert} data-testid="btn-new-vaccinecert-for-patient">
              <FileBadge2 size={14} className="mr-2" /> Create Vaccine Certificate
            </Button>
            <Button variant="outline" onClick={() => setRefOpen(true)} data-testid="btn-new-ref-for-patient">
              Create Referral
            </Button>
            {otherHospitals.length > 0 ? (
              <Button variant="outline" onClick={openSend} data-testid="btn-send-to-hospital">
                <Send size={14} className="mr-2" /> Send to Hospital
              </Button>
            ) : null}
            <Button variant="outline" onClick={openSchedule} data-testid="btn-schedule-visit">
              <Calendar size={14} className="mr-2" /> Schedule next visit
            </Button>
          </div>
        </div>
      </Card>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="hidden md:inline-flex">
          <TabsTrigger value="records" data-testid="tab-records">Medical Records ({records.length})</TabsTrigger>
          <TabsTrigger value="vitals" data-testid="tab-vitals-history">Vitals History ({vitalsHistory.length})</TabsTrigger>
          {canViewClinicalNotes && (
            <TabsTrigger value="notes" data-testid="tab-notes-history">Clinical Notes ({notesHistory.length})</TabsTrigger>
          )}
          <TabsTrigger value="labs" data-testid="tab-lab-results">Lab Results ({labResults.length})</TabsTrigger>
          <TabsTrigger value="rx" data-testid="tab-rx">Prescriptions ({prescriptions.length})</TabsTrigger>
          <TabsTrigger value="referrals" data-testid="tab-referrals">Referrals ({referrals.length})</TabsTrigger>
          <TabsTrigger value="medcerts" data-testid="tab-medcerts">Certificates ({medCerts.length})</TabsTrigger>
          <TabsTrigger value="billing" data-testid="tab-billing">Billing / POS ({posTransactions.length})</TabsTrigger>
        </TabsList>

        {!drilled ? (
          <div className="md:hidden space-y-2">
            {MOBILE_SECTIONS.map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => { setTab(s.key); setDrilled(true); }}
                className="w-full flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4 text-left hover:bg-slate-50 active:scale-[0.99] transition"
                data-testid={`mobile-section-${s.key}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-md bg-slate-100 flex items-center justify-center text-slate-600"><s.icon size={18} /></div>
                  <div className="font-medium text-slate-900">{s.label}</div>
                </div>
                <div className="flex items-center gap-2 text-slate-400">
                  <span className="text-xs font-semibold text-slate-500">{s.count}</span>
                  <ChevronRight size={18} />
                </div>
              </button>
            ))}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setDrilled(false)}
            className="md:hidden flex items-center gap-1 text-sm text-blue-600 font-medium"
            data-testid="mobile-section-back"
          >
            <ArrowLeft size={16} /> Back to sections
          </button>
        )}

        <div className={drilled ? "" : "hidden md:block"}>

        <TabsContent value="records">
          <Card className="border-slate-200">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h2 className="font-semibold text-slate-900" style={{ fontFamily: "Work Sans" }}>Clinical Timeline</h2>
              <Button onClick={openNewRec} className="bg-blue-600 hover:bg-blue-700" size="sm" data-testid="btn-add-record">
                <Plus size={14} className="mr-1" /> Add Record
              </Button>
            </div>
            {records.length === 0 ? (
              <div className="p-10 text-center text-slate-400">No clinical records yet.</div>
            ) : (
              <div className="divide-y divide-slate-200">
                {records.map((r) => {
                  const meta = typeMeta(r.type);
                  const Icon = meta.icon;
                  return (
                    <div key={r.id} className="p-4 flex items-start gap-4 hover:bg-slate-50" data-testid={`record-row-${r.id}`}>
                      <div className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 ${meta.tone}`}><Icon size={16} /></div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-900">{r.title || meta.label}</span>
                          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-slate-100 text-slate-600">{meta.label}</span>
                          <span className="text-xs text-slate-500 ml-auto">{(r.date || "").slice(0, 10)}</span>
                        </div>
                        {r.content ? <div className="mt-1 text-sm text-slate-700 whitespace-pre-line">{r.content}</div> : null}
                        {r.created_by ? <div className="mt-1 text-xs text-slate-500">— {r.created_by}</div> : null}
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Button size="sm" variant="outline" onClick={() => openEditRec(r)} data-testid={`record-edit-${r.id}`}>
                          <Pencil size={14} className="mr-1" /> Edit
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => delRec(r.id)} className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`record-delete-${r.id}`}>
                          <Trash2 size={14} className="mr-1" /> Delete
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="vitals">
          <Card className="border-slate-200">
            <div className="p-4 border-b border-slate-200">
              <h2 className="font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Work Sans" }}>
                <Activity size={16} className="text-emerald-600" /> Vitals History
              </h2>
              <p className="text-xs text-slate-500 mt-1">All vital signs recorded across visits, newest first.</p>
            </div>
            {vitalsHistory.length === 0 ? (
              <div className="p-10 text-center text-slate-400">No vitals recorded yet.</div>
            ) : (
              <div className="divide-y divide-slate-200">
                {vitalsHistory.map((it) => (
                  <div key={it.id} className="p-4" data-testid={`vitals-row-${it.id}`}>
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div className="text-xs text-slate-500">
                        {(it.date || "").slice(0, 10)} {it.visit_date ? `• Visit ${it.visit_date}` : ""}
                      </div>
                      <div className="text-xs text-slate-500">by {it.created_by || "—"}</div>
                    </div>
                    <div className="mt-1 text-sm text-slate-800 font-medium">
                      {vitalsSummary(it.vitals || {}) || it.title || "Vitals recorded"}
                    </div>
                    {it.vitals && (
                      <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2 text-xs text-slate-600">
                        {it.vitals.blood_pressure && <div><span className="text-slate-500">BP:</span> {it.vitals.blood_pressure}</div>}
                        {it.vitals.temperature && <div><span className="text-slate-500">Temp:</span> {it.vitals.temperature}</div>}
                        {it.vitals.heart_rate && <div><span className="text-slate-500">HR:</span> {it.vitals.heart_rate}</div>}
                        {it.vitals.respiratory_rate && <div><span className="text-slate-500">RR:</span> {it.vitals.respiratory_rate}</div>}
                        {it.vitals.oxygen_saturation && <div><span className="text-slate-500">SpO2:</span> {it.vitals.oxygen_saturation}</div>}
                        {it.vitals.weight && <div><span className="text-slate-500">Weight:</span> {it.vitals.weight}</div>}
                        {it.vitals.height && <div><span className="text-slate-500">Height:</span> {it.vitals.height}</div>}
                        {it.vitals.bmi && <div><span className="text-slate-500">BMI:</span> {it.vitals.bmi}</div>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        {canViewClinicalNotes && (
          <TabsContent value="notes">
            <Card className="border-slate-200">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between gap-3">
                <div>
                  <h2 className="font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Work Sans" }}>
                    <NotebookPen size={16} className="text-blue-600" /> Clinical Notes History
                  </h2>
                  <p className="text-xs text-slate-500 mt-1">SOAP notes recorded across visits, newest first.</p>
                </div>
                <Button onClick={openNewClinicalNote} size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="btn-add-clinical-note">
                  <Plus size={14} className="mr-1" /> Add Note
                </Button>
              </div>
              {notesHistory.length === 0 ? (
                <div className="p-10 text-center text-slate-400">No clinical notes recorded yet.</div>
              ) : (
                <div className="divide-y divide-slate-200">
                  {notesHistory.map((it) => {
                    const n = it.clinical_notes || {};
                    const isLegacyNote = !n.subjective && !n.objective && !n.assessment && !n.plan && !n.notes;
                    const openIt = () => openEditClinicalNote(it);
                    const isOpen = expandedNotes.has(it.id);
                    const soap = [
                      { k: "S", label: "Subjective", v: n.subjective },
                      { k: "O", label: "Objective", v: n.objective },
                      { k: "A", label: "Assessment", v: n.assessment },
                      { k: "P", label: "Plan", v: n.plan },
                    ];
                    const clamp2 = { display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" };
                    return (
                      <div key={it.id} className="p-4" data-testid={`notes-row-${it.id}`}>
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                          <div className="text-xs text-slate-500">
                            {(it.date || "").slice(0, 10)} {it.visit_date ? `• Visit ${it.visit_date}` : ""}
                          </div>
                          <div className="text-xs text-slate-500">by {it.created_by || "—"}</div>
                        </div>

                        {isLegacyNote ? (
                          <div className="mt-2 text-sm cursor-pointer" onClick={openIt}>
                            {it.title && <div className="font-semibold text-slate-800">{it.title}</div>}
                            {it.content && <div className="text-slate-700 whitespace-pre-line">{it.content}</div>}
                          </div>
                        ) : !isOpen ? (
                          // Collapsed: only the Subjective preview + expand affordance.
                          <button type="button" onClick={() => toggleNote(it.id)} className="mt-2 w-full text-left rounded-lg hover:bg-slate-50 -mx-1 px-1 py-1" data-testid={`note-collapsed-${it.id}`}>
                            <div className="flex items-start gap-2 text-sm">
                              <span className="font-bold text-blue-700 shrink-0 w-4">S</span>
                              <span className="text-slate-700 flex-1" style={clamp2}>
                                {n.subjective || <span className="italic text-slate-400">No subjective note</span>}
                              </span>
                              <ChevronDown size={16} className="text-slate-400 shrink-0 mt-0.5" />
                            </div>
                            <div className="text-[11px] text-slate-400 mt-1 pl-6">Tap to view full S · O · A · P</div>
                          </button>
                        ) : (
                          // Expanded: stacked S / O / A / P (+ extra Notes).
                          <button type="button" onClick={() => toggleNote(it.id)} className="mt-2 w-full text-left space-y-3" data-testid={`note-expanded-${it.id}`}>
                            {soap.map((row) => (
                              <div key={row.k}>
                                <div className="font-bold text-blue-700 text-sm">{row.k} <span className="font-normal text-slate-400 text-xs">· {row.label}</span></div>
                                <div className="text-slate-700 whitespace-pre-line text-sm mt-0.5">{row.v || <span className="italic text-slate-400">—</span>}</div>
                              </div>
                            ))}
                            {n.notes && (
                              <div>
                                <div className="font-bold text-blue-700 text-sm">Notes</div>
                                <div className="text-slate-700 whitespace-pre-line text-sm mt-0.5">{n.notes}</div>
                              </div>
                            )}
                            <div className="text-[11px] text-slate-400 flex items-center gap-1"><ChevronUp size={13} /> Tap to collapse</div>
                          </button>
                        )}

                        <div className="mt-3 flex justify-end gap-2">
                          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); deleteClinicalNote(it.id); }} className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`clinical-note-delete-${it.id}`}>
                            <Trash2 size={14} className="mr-1" /> Delete
                          </Button>
                          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); openIt(); }} data-testid={`clinical-note-edit-${it.id}`}>
                            <Pencil size={14} className="mr-1" /> Edit
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </TabsContent>
        )}

        <TabsContent value="labs">
          <LabAttachmentsPanel
            patientId={id}
            attachments={labResults}
            onChanged={loadAll}
          />
        </TabsContent>

        <TabsContent value="rx">
          <Card className="border-slate-200 overflow-hidden">
            {prescriptions.length === 0 ? (
              <div className="p-10 text-center text-slate-400">No prescriptions yet.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Rx #</th>
                    <th className="px-4 py-3 text-left font-semibold">Date</th>
                    <th className="px-4 py-3 text-left font-semibold">Meds</th>
                    <th className="px-4 py-3 text-left font-semibold">Status</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {prescriptions.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3 mono text-xs">{r.prescription_number}</td>
                      <td className="px-4 py-3 text-xs text-slate-700">{(r.date || "").slice(0, 10)}</td>
                      <td className="px-4 py-3">{(r.medicines || []).length}</td>
                      <td className="px-4 py-3"><span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-slate-100 text-slate-600">{r.status}</span></td>
                      <td className="px-4 py-3 text-right">
                        <Button asChild size="sm" variant="outline"><Link to={`/prescriptions/${r.id}/preview`}>View</Link></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="referrals">
          <Card className="border-slate-200">
            {referrals.length === 0 ? (
              <div className="p-10 text-center text-slate-400">No referrals yet.</div>
            ) : (
              <div className="divide-y divide-slate-200">
                {referrals.map((r) => (
                  <div key={r.id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                    <div>
                      <div className="font-semibold text-slate-900">{r.referred_to_name} <span className="text-xs text-slate-500 font-normal">· {r.referred_to_specialty}</span></div>
                      <div className="text-xs text-slate-500">{(r.date || "").slice(0, 10)} · {r.reason_for_referral}</div>
                    </div>
                    <Button asChild size="sm" variant="outline"><Link to={`/referrals/${r.id}/preview`}>View</Link></Button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="medcerts">
          <Card className="border-slate-200">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between gap-3">
              <div>
                <h2 className="font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Work Sans" }}>
                  <FileBadge2 size={16} className="text-blue-600" /> Certificates
                </h2>
                <p className="text-xs text-slate-500 mt-1">Medical and vaccine certificates created from this patient record.</p>
              </div>
              <div className="flex gap-2">
                <Button onClick={openNewMedCert} size="sm" variant="outline" data-testid="btn-add-medcert">
                  <Plus size={14} className="mr-1" /> Medical
                </Button>
                <Button onClick={openNewVaccineCert} size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="btn-add-vaccinecert">
                  <Plus size={14} className="mr-1" /> Vaccine
                </Button>
              </div>
            </div>

            {medCerts.length === 0 ? (
              <div className="p-10 text-center text-slate-400">No certificates yet.</div>
            ) : (
              <div className="divide-y divide-slate-200">
                {medCerts.map((c) => (
                  <div key={c.id} className="p-4 flex items-center justify-between gap-3" data-testid={`medcert-row-${c.id}`}>
                    <div>
                      <div className="font-semibold text-slate-900 flex items-center gap-2">
                        {c.issue_date || (c.created_at || "").slice(0, 10)}
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${c.cert_type === "vaccine" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>
                          {c.cert_type === "vaccine" ? "Vaccine" : "Medical"}
                        </span>
                      </div>
                      <div className="text-sm text-slate-600">{c.diagnosis || (c.cert_type === "vaccine" ? "No vaccine details entered" : "No diagnosis entered")}</div>
                      <div className="text-xs text-slate-500">Created by {c.created_by_name || "—"}</div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button size="sm" variant="outline" onClick={() => openEditMedCert(c)} data-testid={`medcert-edit-${c.id}`}>
                        <Pencil size={14} className="mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openMedCert(c)} data-testid={`medcert-view-${c.id}`}>
                        <Eye size={14} className="mr-1" /> View / Print
                      </Button>
                      {canDeleteCert ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => deleteMedCert(c)}
                          data-testid={`medcert-delete-${c.id}`}
                        >
                          <Trash2 size={14} className="mr-1" /> Delete
                        </Button>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="billing">
          <Card className="border-slate-200" data-testid="billing-tab-card">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="font-semibold text-slate-900" style={{ fontFamily: "Work Sans" }}>Billing / POS</h2>
              <Button
                size="sm"
                onClick={async () => {
                  try {
                    const { data } = await api.post("/pos/transactions", {
                      patient_id: id,
                      line_items: [],
                      status: "draft",
                    });
                    toast.success(`Created ${data.transaction_number}`);
                    nav(`/pos/transactions/${data.id}`);
                  } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
                }}
                data-testid="btn-new-bill"
              >
                <Plus size={14} className="mr-1" /> New Bill
              </Button>
            </div>
            {posTransactions.length === 0 ? (
              <div className="p-10 text-center text-slate-400">No billing transactions yet.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Invoice #</th>
                    <th className="text-left px-4 py-2 font-medium">Status</th>
                    <th className="text-right px-4 py-2 font-medium">Total</th>
                    <th className="text-left px-4 py-2 font-medium">Date</th>
                    <th className="text-right px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {posTransactions.map((t) => (
                    <tr key={t.id} data-testid={`billing-row-${t.id}`}>
                      <td className="px-4 py-2 font-medium">{t.transaction_number}</td>
                      <td className="px-4 py-2 capitalize">{t.status}</td>
                      <td className="px-4 py-2 text-right">₱{(Number(t.grand_total) || 0).toFixed(2)}</td>
                      <td className="px-4 py-2 text-xs text-slate-500">{(t.created_at || "").slice(0, 10)}</td>
                      <td className="px-4 py-2 text-right">
                        <Button size="sm" variant="outline" onClick={() => nav(`/pos/transactions/${t.id}`)} data-testid={`btn-open-bill-${t.id}`}>Open</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </TabsContent>
        </div>
      </Tabs>

      {/* Record Dialog */}
      <Dialog open={recOpen} onOpenChange={setRecOpen}>
        <DialogContent data-testid="record-dialog">
          <DialogHeader><DialogTitle>{recEditingId ? "Edit Record" : "Add Medical Record"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={recForm.type} onValueChange={(v) => setRecForm({ ...recForm, type: v })}>
                <SelectTrigger data-testid="record-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RECORD_TYPES.filter((t) => t.value !== "note" || recForm.type === "note").map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input type="date" value={recForm.date} onChange={(e) => setRecForm({ ...recForm, date: e.target.value })} /></div>
            <div className="col-span-2"><Label>Content / Details</Label><Textarea rows={6} value={recForm.content} onChange={(e) => setRecForm({ ...recForm, content: e.target.value })} placeholder="e.g. BP 150/95, Hypertension Stage 1, findings, etc." data-testid="record-content" /></div>
          </div>
          <DialogFooter className="px-5 py-3 border-t border-slate-200">
            <Button variant="outline" onClick={() => setRecOpen(false)}>Cancel</Button>
            <Button onClick={saveRec} className="bg-blue-600 hover:bg-blue-700" data-testid="record-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={noteOpen} onOpenChange={setNoteOpen}>
        <DialogContent className="max-w-3xl" data-testid="clinical-note-editor">
          <DialogHeader>
            <DialogTitle>
              {noteEditingId ? "Edit Clinical Note" : "Add Clinical Note"}
            </DialogTitle>
          </DialogHeader>

          <div className="max-h-[65vh] overflow-auto">
            <ClinicalNotesForm
              value={noteForm}
              onChange={setNoteForm}
              idPrefix="patient-note"
              objectivePrefill={latestVitalsObjectiveText()}
            />
          </div>

          <DialogFooter className="px-5 py-3 border-t border-slate-200">
            <Button variant="outline" onClick={() => setNoteOpen(false)} disabled={noteSaving}>
              Cancel
            </Button>
            <Button onClick={saveClinicalNote} disabled={noteSaving} data-testid="btn-save-clinical-note">
              {noteSaving ? "Saving..." : noteEditingId ? "Update Note" : "Add Note"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Medical Certificate Editor */}
      <Dialog open={mcOpen} onOpenChange={setMcOpen}>
        <DialogContent className="w-[96vw] max-w-[96vw] h-[94vh] max-h-[94vh] overflow-hidden p-0" data-testid="patient-medcert-dialog">
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-200">
            <DialogTitle>{mcEditId ? "Edit " : "Create "}{mcType === "vaccine" ? "Vaccine Certificate" : "Medical Certificate"}</DialogTitle>
          </DialogHeader>

          <div className="grid h-[calc(94vh-125px)] overflow-hidden" style={{ gridTemplateColumns: "420px minmax(0, 1fr)" }}>
            <div className="space-y-3 overflow-y-auto px-5 py-4 border-r border-slate-200">
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <Label>Patient</Label>
                  <Input disabled value={patient.full_name || ""} data-testid="mc-patient-name" />
                </div>
                <div>
                  <Label>Date</Label>
                  <Input type="date" value={mcForm.issue_date} onChange={(e) => setMcForm({...mcForm, issue_date: e.target.value})} data-testid="mc-issue-date" />
                </div>
              </div>

              <div>
                <Label>Date as printed (optional)</Label>
                <Input
                  value={mcForm.date_text}
                  onChange={(e) => setMcForm({ ...mcForm, date_text: e.target.value })}
                  placeholder={mcForm.issue_date ? new Date(mcForm.issue_date).toLocaleDateString("en-PH", { year: "numeric", month: "long", day: "numeric" }) : "e.g. July 16, 2026"}
                  data-testid="mc-date-text"
                />
                <div className="text-[11px] text-slate-400 mt-1">Leave blank to print the date above. Type here to override how the date appears (any wording).</div>
              </div>

              <div>
                <Label>Address</Label>
                <Input
                  disabled
                  value={patient.address || patient.complete_address || patient.full_address || patient.home_address || [patient.street, patient.barangay, patient.city, patient.province].filter(Boolean).join(", ") || ""}
                  data-testid="mc-patient-address"
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Age</Label>
                  <Input disabled value={patient.age || ""} />
                </div>
                <div>
                  <Label>Sex</Label>
                  <Input disabled value={patient.gender || ""} />
                </div>
                {mcType !== "vaccine" ? (
                  <div>
                    <Label>Bed Rest Days</Label>
                    <Input type="number" min="0" value={mcForm.rest_days} onChange={(e) => setMcForm({...mcForm, rest_days: e.target.value})} data-testid="mc-rest-days" />
                  </div>
                ) : null}
              </div>

              <div>
                <Label>{mcType === "vaccine" ? "Vaccine Details" : "Diagnosis"}</Label>
                <Textarea rows={3} value={mcForm.diagnosis} onChange={(e) => setMcForm({...mcForm, diagnosis: e.target.value})} placeholder={mcType === "vaccine" ? "Vaccine name, dose, date given, lot no., etc." : ""} data-testid="mc-diagnosis" />
              </div>

              <div>
                <Label>Recommendations</Label>
                <Textarea rows={3} value={mcForm.recommendations} onChange={(e) => setMcForm({...mcForm, recommendations: e.target.value})} placeholder="Additional advice, restrictions, follow-up instructions..." data-testid="mc-recommendations" />
              </div>

              <div>
                <Label>Purpose</Label>
                <Input value={mcForm.purpose} onChange={(e) => setMcForm({...mcForm, purpose: e.target.value})} placeholder="Work, school, travel, etc." data-testid="mc-purpose" />
              </div>

              <div>
                <Label>Remarks</Label>
                <Textarea rows={2} value={mcForm.remarks} onChange={(e) => setMcForm({...mcForm, remarks: e.target.value})} data-testid="mc-remarks" />
              </div>

              <div>
                <Label>Template Editor</Label>
                <Textarea
                  rows={12}
                  value={mcForm.body_template}
                  onChange={(e) => setMcForm({...mcForm, body_template: e.target.value})}
                  data-testid="mc-template-editor"
                />
                <div className="text-xs text-slate-500 mt-1">
                  Placeholders: {"{{patient_name}}"}, {"{{age}}"}, {"{{gender}}"}, {"{{address}}"}, {"{{date}}"}, {"{{diagnosis}}"}, {"{{rest_days}}"}, {"{{he_she}}"}, {"{{recommendations}}"}, {"{{remarks}}"}, {"{{purpose}}"}
                </div>
              </div>
            </div>

            <div className="bg-slate-100 overflow-auto p-4 flex justify-center items-start min-w-0">
              <MedicalCertificatePreview
                scale={0.68}
                certificate={{
                  ...mcForm,
                  id: "preview",
                  patient_snapshot: patient,
                  doctor_snapshot: {
                    ...(user || {}),
                    ...(doctorRxProfile || {}),
                    name: doctorRxProfile?.doctor_name || doctorRxProfile?.name || user?.doctor_name || user?.name || "",
                    doctor_name: doctorRxProfile?.doctor_name || doctorRxProfile?.name || user?.doctor_name || user?.name || "",
                    email: user?.email || "",
                    role: user?.role || "",
                    specialty: doctorRxProfile?.specialty || user?.specialty || "",
                    license_number: doctorRxProfile?.license_number || user?.license_number || "",
                    ptr_number: doctorRxProfile?.ptr_number || user?.ptr_number || "",
                    s2_number: doctorRxProfile?.s2_number || user?.s2_number || "",
                    affiliations: doctorRxProfile?.affiliations || user?.affiliations || [],
                    signature_image_url: doctorRxProfile?.signature_image_url || user?.signature_image_url || "",
                  },
                  created_at: new Date().toISOString(),
                }}
                settings={printSettings}
              />
            </div>
          </div>

          <DialogFooter className="px-5 py-3 border-t border-slate-200">
            <Button variant="outline" onClick={() => setMcOpen(false)}>Cancel</Button>
            <Button onClick={() => saveMedCert(false)} disabled={mcSaving} variant="outline" data-testid="btn-save-medcert">
              <Save size={14} className="mr-1" /> Save
            </Button>
            <Button onClick={() => saveMedCert(true)} disabled={mcSaving} data-testid="btn-save-preview-medcert">
              <Printer size={14} className="mr-1" /> Save & Preview
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Medical Certificate Preview */}
      <Dialog open={mcPreviewOpen} onOpenChange={setMcPreviewOpen}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="patient-medcert-preview-dialog">
          <DialogHeader>
            <DialogTitle>{activeCert?.cert_type === "vaccine" ? "Vaccine Certificate Preview" : "Medical Certificate Preview"}</DialogTitle>
          </DialogHeader>
          <div className="bg-slate-100 p-4">
            <MedicalCertificatePreview certificate={activeCert} settings={printSettings} scale={0.78} />
          </div>
          <DialogFooter className="px-5 py-3 border-t border-slate-200">
            <Button variant="outline" onClick={() => setMcPreviewOpen(false)}>Close</Button>
            <Button onClick={() => window.print()} data-testid="btn-print-patient-medcert">
              <Printer size={14} className="mr-1" /> Print {activeCert?.cert_type === "vaccine" ? "Vaccine Certificate" : "Medical Certificate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ScheduleVisitDialog
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        patientId={id}
        patientName={patient?.full_name}
        initialDate={patient?.next_visit_date || ""}
        initialTime={patient?.next_visit_time || ""}
        initialDoctorId={patient?.next_visit_doctor_id || ""}
        onSaved={loadAll}
      />

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent data-testid="send-to-hospital-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Send size={16} className="text-blue-600" /> Send Patient to Hospital</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-slate-600">
              Copies <b>{patient.full_name}</b> and all their records (prescriptions, clinical notes, vitals, labs, certificates, referrals) to another hospital you are affiliated with.
            </div>
            <div>
              <Label>Destination hospital</Label>
              <select
                value={sendTarget}
                onChange={(e) => runSendPreview(e.target.value)}
                className="mt-1 w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm"
                data-testid="send-target-select"
              >
                <option value="">Select a hospital…</option>
                {otherHospitals.map((m) => (
                  <option key={m.hospital_id} value={m.hospital_id}>{m.hospital_name}</option>
                ))}
              </select>
            </div>

            {sendBusy && !sendPreview ? <div className="text-sm text-slate-500">Checking…</div> : null}

            {sendPreview ? (
              <div className="rounded-md border border-slate-200 p-3 text-sm space-y-2">
                <div className="text-slate-700">
                  Will copy to <b>{sendPreview.target_hospital_name}</b>:{" "}
                  {Object.entries(sendPreview.counts || {}).filter(([, v]) => v > 0).map(([k, v]) => `${v} ${k}`).join(", ") || "patient record only"}.
                </div>
                {sendPreview.match ? (
                  <div className="rounded bg-amber-50 border border-amber-200 p-2">
                    <div className="text-amber-900 font-medium mb-1">A matching patient already exists there:</div>
                    <div className="text-amber-800 text-xs mb-2">{sendPreview.match.full_name} · {sendPreview.match.contact_number || "no contact"}</div>
                    <label className="flex items-center gap-2 mb-1">
                      <input type="radio" name="sendmode" checked={sendMode === "merge"} onChange={() => setSendMode("merge")} /> Merge records into the existing patient
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="radio" name="sendmode" checked={sendMode === "new"} onChange={() => setSendMode("new")} /> Create a new patient anyway
                    </label>
                  </div>
                ) : (
                  <div className="text-emerald-700 text-xs">No matching patient found — a new patient will be created.</div>
                )}
              </div>
            ) : null}

            <div className="text-xs text-slate-400">This copies protected health information into another hospital and is recorded in the audit log.</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)} disabled={sendBusy}>Cancel</Button>
            <Button onClick={confirmSend} disabled={sendBusy || !sendTarget || (!!sendPreview?.match && !sendMode)} data-testid="btn-confirm-send">
              <Send size={14} className="mr-2" /> {sendBusy ? "Sending…" : "Send"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ReferralDialog
        open={refOpen}
        onOpenChange={setRefOpen}
        fixedPatientId={id}
        patientOptions={[patient]}
        onSaved={loadAll}
      />
    </div>
  );
}
